import { CurrencyPipe, DatePipe, DecimalPipe, LowerCasePipe, PercentPipe, UpperCasePipe } from '@angular/common';
import * as c from '@angular/compiler';
import { AbstractType, InjectionToken, Injector, PipeTransform, Type } from '@angular/core';
import _ from 'lodash';
import { TranslatePipe } from '@ngx-translate/core';

type Context = unknown;

function unsupported(ast: c.AST): never {
	throw new Error(`${ast.constructor.name} is not supported`);
}

export class AstVistor implements c.AstVisitor {

	private pipes = new Map<string, PipeTransform>();

	constructor(private injector: Injector, private translatePipe: TranslatePipe) {
		this.pipes.set('translate', translatePipe);
	}

	visitLiteralPrimitive(ast: c.LiteralPrimitive): unknown {
		return ast.value;
	}

	visitInterpolation(ast: c.Interpolation, ctx: Context): unknown {
		const parts: string[] = [];
		for (let i = 0; i < ast.strings.length; i++) {
			const string = ast.strings[i];

			if (string.length > 0) {
				parts.push(string);
			}

			if (i < ast.expressions.length) {
				let value = ast.expressions[i].visit(this, ctx);
				if (value === null || _.isUndefined(value)) {
					value = '';
				}
				parts.push(value);
			}
		}
		return parts.join('');
	}

	visitImplicitReceiver(ast: c.ImplicitReceiver, ctx: Context): unknown {
		return ctx;
	}

	visitPropertyRead(ast: c.PropertyRead, ctx: Context): unknown {
		const receiver = ast.receiver.visit(this, ctx);
		return receiver?.[ast.name];
	}

	visitBinary(ast: c.Binary, ctx: Context): unknown {
		let op: (left: number, right: number) => unknown;
		switch (ast.operation) {
			case '+':
				op = (a, b) => a + b;
				break;
			case '-':
				op = (a, b) => a - b;
				break;
			case '*':
				op = (a, b) => a * b;
				break;
			case '/':
				op = (a, b) => a / b;
				break;
			case '%':
				op = (a, b) => a % b;
				break;
			case '&&':
				op = (a, b) => a && b;
				break;
			case '||':
				op = (a, b) => a || b;
				break;
			case '==':
				op = (a, b) => a == b;
				break;
			case '!=':
				op = (a, b) => a != b;
				break;
			case '===':
				op = (a, b) => a === b;
				break;
			case '!==':
				op = (a, b) => a !== b;
				break;
			case '<':
				op = (a, b) => a < b;
				break;
			case '>':
				op = (a, b) => a > b;
				break;
			case '<=':
				op = (a, b) => a <= b;
				break;
			case '>=':
				op = (a, b) => a >= b;
				break;
			default:
				throw new Error(`Unsupported operation ${ast.operation}`);
		}
		const left = ast.left.visit(this, ctx);
		const right = ast.right.visit(this, ctx);
		return op(left, right);
	}

	visitUnary(ast: c.Unary, ctx: Context): unknown {
		let op: (expr: number) => unknown;
		switch (ast.operator) {
			case '+':
				op = (a) => a;
				break;
			case '-':
				op = (a) => -a;
				break;
			default:
				throw new Error(`Unsupported operator ${ast.operator}`);
		}
		const expr = ast.expr.visit(this, ctx);
		return op(expr);
	}

	visitChain(ast: c.Chain, ctx: Context): unknown {
		unsupported(ast);
	}

	visitConditional(ast: c.Conditional, ctx: Context): unknown {
		unsupported(ast);
	}

	visitFunctionCall(ast: c.FunctionCall, ctx: Context): unknown {
		unsupported(ast);
	}

	visitThisReceiver?(ast: c.ThisReceiver, ctx: Context): unknown {
		unsupported(ast);
	}

	visitKeyedRead(ast: c.KeyedRead, ctx: Context): unknown {
		unsupported(ast);
	}

	visitKeyedWrite(ast: c.KeyedWrite, ctx: Context): unknown {
		unsupported(ast);
	}

	visitLiteralArray(ast: c.LiteralArray, ctx: Context): unknown {
		unsupported(ast);
	}

	visitLiteralMap(ast: c.LiteralMap, ctx: Context): unknown {
		unsupported(ast);
	}

	visitMethodCall(ast: c.MethodCall, ctx: Context): unknown {
		unsupported(ast);
	}

	private resolvePipe<T extends PipeTransform>(name: string, type: Type<T> | InjectionToken<T> | AbstractType<T>): PipeTransform {
		let pipe = this.pipes.get(name);
		if (!pipe) {
			pipe = this.injector.get(type);
			this.pipes.set(name, pipe);
		}
		return pipe;
	}

	private getPipe(name: string): PipeTransform {
		switch (name) {
			case 'currency':
				return this.resolvePipe(name, CurrencyPipe);
			case 'decimal':
				return this.resolvePipe(name, DecimalPipe);
			case 'number':
				return this.resolvePipe(name, DecimalPipe);
			case 'translate':
				return this.resolvePipe(name, TranslatePipe);
			case 'date':
				return this.resolvePipe(name, DatePipe);
			case 'lowercase':
				return this.resolvePipe(name, LowerCasePipe);
			case 'uppercase':
				return this.resolvePipe(name, UpperCasePipe);
			case 'percent':
				return this.resolvePipe(name, PercentPipe);
			default:
				throw Error(`Unsupported pipe ${name}`);
		}
	}

	visitPipe(ast: c.BindingPipe, ctx: Context): unknown {
		const pipe = this.getPipe(ast.name);
		const expr = ast.exp.visit(this, ctx);
		const args = ast.args.map(arg => arg.visit(this, ctx));
		return pipe.transform(expr, ...args);
	}
	visitPrefixNot(ast: c.PrefixNot, ctx: Context): unknown {
		unsupported(ast);
	}
	visitNonNullAssert(ast: c.NonNullAssert, ctx: Context): unknown {
		unsupported(ast);
	}
	visitPropertyWrite(ast: c.PropertyWrite, ctx: Context): unknown {
		unsupported(ast);
	}
	visitQuote(ast: c.Quote, ctx: Context): unknown {
		unsupported(ast);
	}
	visitSafeMethodCall(ast: c.SafeMethodCall, ctx: Context): unknown {
		unsupported(ast);
	}
	visitSafePropertyRead(ast: c.SafePropertyRead, ctx: Context): unknown {
		unsupported(ast);
	}
	visitASTWithSource?(ast: c.ASTWithSource, ctx: Context): unknown {
		unsupported(ast);
	}
}
